import { defineStore } from 'pinia';
import { ref } from 'vue';

/**
 * In-flight track-list exports, so the "your export is ready" toast can fire
 * wherever the user happens to be.
 *
 * This store exists because `useAsyncOperationSubscription` tears its Echo
 * subscription down in `onUnmounted`, and every other caller of it is a
 * component that dies with the inspector. The export is started from the
 * inspector but must be announced even after the user has closed it and
 * navigated elsewhere — so the subscription is hosted by
 * `TrackListExportHost`, mounted in `App.vue` outside `<RouterView>`, and this
 * store is what tells it which operations to watch.
 *
 * Persisted to localStorage so a page reload mid-export re-subscribes instead
 * of orphaning the operation.
 */

export interface InFlightTrackListExport {
  operationId: string;
  /** Scope suffix for the polling fallback (see the backend's per-request nonce). */
  scope: string;
  /** "Bucket - 90s Alt Rock", for the toast. */
  label: string;
  /** Epoch ms, used to prune entries whose operation row has certainly expired. */
  startedAt: number;
}

/**
 * Matches `AsyncOperationRegistrar::TTL_SECONDS`. Past this the operation row is
 * gone from Redis, so neither Echo nor polling can ever resolve it and holding
 * the entry would mean polling forever.
 */
const OPERATION_TTL_MS = 3600 * 1000;

/** Belt-and-braces cap so a runaway client can't accumulate subscriptions. */
const MAX_TRACKED_EXPORTS = 20;

export const useTrackListExportStore = defineStore(
  'trackListExport',
  () => {
    const inFlightExports = ref<InFlightTrackListExport[]>([]);

    /**
     * An entry is only usable if it can actually be resolved — that needs both
     * an operation id (to subscribe) and a scope (to poll).
     *
     * This is a real guard, not defensive noise. The list is rehydrated from
     * localStorage, which is untrusted input: it survives deploys that change
     * this shape, and a response the client failed to parse can write a partial
     * entry. Without this check a half-written entry reaches the watcher, whose
     * required props then fail and throw during setup — and because the host is
     * mounted in App.vue, that unhandled setup error blanks the ENTIRE app.
     * A stale export must never be able to do that.
     */
    function isUsable(candidate: unknown): candidate is InFlightTrackListExport {
      if (candidate === null || typeof candidate !== 'object') {
        return false;
      }

      const entry = candidate as Partial<InFlightTrackListExport>;

      return typeof entry.operationId === 'string'
        && entry.operationId !== ''
        && typeof entry.scope === 'string'
        && entry.scope !== ''
        && typeof entry.startedAt === 'number'
        && Number.isFinite(entry.startedAt);
    }

    /** Drop malformed entries, and anything too old for its operation row to exist. */
    function pruneExpired(now: number = Date.now()): void {
      inFlightExports.value = inFlightExports.value.filter(
        (inFlightExport) => isUsable(inFlightExport) && now - inFlightExport.startedAt < OPERATION_TTL_MS,
      );
    }

    function track(inFlightExport: InFlightTrackListExport): void {
      pruneExpired();

      // Refuse at the door too, so a bad entry never reaches storage in the
      // first place.
      if (!isUsable(inFlightExport)) {
        return;
      }

      if (inFlightExports.value.some((existing) => existing.operationId === inFlightExport.operationId)) {
        return;
      }

      inFlightExports.value = [...inFlightExports.value, inFlightExport].slice(-MAX_TRACKED_EXPORTS);
    }

    /** Stop watching an operation — it finished, failed, or aged out. */
    function forget(operationId: string): void {
      inFlightExports.value = inFlightExports.value.filter(
        (inFlightExport) => inFlightExport.operationId !== operationId,
      );
    }

    return { inFlightExports, track, forget, pruneExpired };
  },
  {
    persist: {
      storage: localStorage,
      key: 'trackListExportStore',
      pick: ['inFlightExports'],
    },
  },
);
